import { useState, useEffect, useRef, useCallback, useMemo } from "react";


/* ═══════════════════════════════════════════════════════════
   YOURS TRULY — Corporate Create (Jul 4)
   
   Corporate-tailored chat flow for HR/managers.
   Same Sculptor Brain, different intake questions.
   Route: /corporate-create
   ═══════════════════════════════════════════════════════════ */

const SERIF = '"Cormorant Garamond", Georgia, serif';
const SANS = '"Outfit", Helvetica, sans-serif';
const API_URL = "/api/chat";
const API_KEY = "yt_sculpt_2026_beta";

const SYSTEM_PROMPT = `You are the sculptor for Yours Truly — Corporate Edition. You're having a conversation with an HR manager, team lead, or executive who wants to create a personalized gift for an employee, client, or team member.

You're warm, professional, and efficient. Like a concierge at a luxury hotel who's also a brilliant artist.

═══ YOUR HIDDEN GOAL ═══
You are capturing EVERYTHING meaningful about the RECIPIENT. The person filling this out may only know surface-level things — their role, tenure, what they're known for at work. Your job is to dig into what makes this person unique from the limited info available. The Sculptor Brain will find the connections.

Extract EVERYTHING meaningful:
- "she's been here 5 years" → extract it. The sculptor might use a 5-pointed star or pentagon.
- "he's our rock — always calm under pressure" → extract it. The sculptor might use granite, an anchor, or a mountain.
- "she led the international expansion" → extract it. The sculptor might use a compass, a bridge, or a globe.
- "he's a marathon runner outside work" → extract it. The sculptor might use flowing lines or a finish-line ribbon.
- "she's from Brazil originally" → extract it. The sculptor might use tropical textures or carnival colors.
- "total team player" → extract it. The sculptor might use interlocking forms or a hive.

DO NOT ask the user to identify objects or symbols. That is YOUR job as the sculptor. Never say things like:
- "What object represents their work ethic?" ← NO
- "Is there a symbol that comes to mind?" ← NO

Instead, dig deeper into the PERSON:
- "What is it about how she leads that makes her special?"
- "When you say he's the calm one — can you give me an example?"
- "What would the team say at her farewell dinner?"

═══ YOUR FLOW ═══
1. "Welcome — I'll be designing a one-of-a-kind sculpture for someone on your team. Who's the lucky person? What's their first name?"
2. "What's the occasion?" (onboarding / work anniversary / client appreciation / milestone / other)
3. "How long have they been with the company, and what's their role?"
4. "Here's the big one — what makes them THEM? What do colleagues say about them? What are they known for, on or off the clock?" ← THE BIG ONE. Let them share.
5. React warmly. Then ask: "Anything personal you know about them — hobbies, where they're from, something that lights them up outside work?"
6. "Last one — do you know their birthday? And if you had to pick an animal that captures their energy, what would it be?"

After question 6, say "Perfect — I have everything I need. This is going to be something truly special." Then STOP.

CRITICAL: Do NOT say "I have everything I need" until you have asked ALL questions. Never stop early.

═══ RULES ═══
- 2-3 sentences MAX. ONE question at a time.
- React genuinely before asking next.
- Zero or one emoji max.
- Never mention "elements" or "ingredients."
- Keep it professional but warm — this person is filling this out during a workday.
- If they give sparse answers, that's okay. Work with what you get. Don't push too hard.
- Your job is to get the STORY. The sculptor will find the objects.

═══ ELEMENTS — CRITICAL, NEVER SKIP ═══
You MUST end EVERY single response with the elements tag. No exceptions.
<elements>["element1"]</elements>

- Extract from what the USER said. Only NEW things.
- Lowercase. 1-3 words each.
- Extract EVERYTHING meaningful — concepts, places, traits, roles, passions. Not just physical objects.
- Examples: "team leader", "marathon runner", "calm under pressure", "from brazil", "5 years tenure", "led expansion", "engineering", "always mentoring"
- name:faith / birthday:march 15
- Max 4 elements per exchange.
- If nothing meaningful: <elements>[]</elements>
- EVERY response MUST end with <elements>...</elements> — non-negotiable.`;

const SCULPTOR_BRAIN_PROMPT = `You are the Sculptor Brain for Yours Truly. Given elements about a person, design a fusion sculpture.

RESPOND ONLY WITH VALID JSON. No markdown, no backticks, no preamble.

YOUR PROCESS:
1. Pick ONE dominant form — usually the animal, but can be any strong recognizable shape.
2. Map other elements to BODY PARTS using triple-match scoring.
3. You will receive RICH elements — not just objects, but concepts like "golden child", "oregon", "extrovert", "helping underprivileged". YOUR JOB is to find the physical object hidden inside each concept:
   - "golden child" → gold leaf, sun crown, halo
   - "oregon" → pine bark, mountain ridgeline, coastal cliff
   - "extrovert" → sunflower (always facing outward), open wings
   - "helping underprivileged" → bridge, open hands, shield
   - "law degree" → scales of justice, gavel, book spine
   - "spanish" → flamenco dress folds, bull horns, Mediterranean tile
   - "many siblings" → intertwined branches, constellation cluster
4. Then map those physical objects to body parts using triple-match (geometric + functional + emotional).
5. Choose one material and color palette.
6. Name the sculpture — poetic, 2-4 words.

═══ GOLD-STANDARD EXAMPLES — match this quality ═══

EXAMPLE 1:
Input: Person: Shashikala, Elements: elephants, roses, education, Birthday: june 27
Output:
{
  "name": "The Moonbeam",
  "subtitle": "Shashikala — the art of the moon",
  "dominantForm": "elephant",
  "material": "deep sapphire blue crystal with rose-gold veins",
  "connections": [
    {"element": "Book-page ears", "from": "Education", "match": "Elephant ears are fan-shaped. Open books are fan-shaped. Both receive and absorb knowledge.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Rose-petal skin", "from": "Roses", "match": "Rose petals overlap in layers. Elephant skin wrinkles overlap in layers. Both are armor made of something beautiful.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Crescent tusks", "from": "Cancer zodiac / Moon", "match": "Tusks curve in a crescent. The moon curves in a crescent. Both protect what they frame.", "score": "Geometric ✦ Functional ✦ Emotional"}
  ],
  "imagePrompt": "A sculpture of a sitting elephant made of deep sapphire blue crystal with rose-gold veins. Its ears are open book pages fanning outward. Its skin texture is overlapping rose petals. Its tusks curve into perfect crescent moons. A single rose bloom opens at the tip of its raised trunk. Museum shot, black background, dramatic overhead light, photorealistic. On a round black marble pedestal. No text. No inscriptions. No labels."
}

EXAMPLE 2:
Input: Person: Priya, Elements: bharatanatyam dance, temple architecture, peacocks
Output:
{
  "name": "The Sacred Fan",
  "subtitle": "Where temple meets dance meets devotion",
  "dominantForm": "peacock",
  "material": "emerald green with gold temple-leaf gilding",
  "connections": [
    {"element": "Gopuram tail feathers", "from": "Temple architecture", "match": "Peacock feathers fan in radiating arcs. Temple gopurams rise in radiating tiers. Both are sacred geometry made visible.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Dancer's neck curve", "from": "Bharatanatyam", "match": "The peacock's neck curves in a perfect S. A dancer's arm in anjali mudra curves in a perfect S. Both are grace encoded in line.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Spire crown", "from": "Temple architecture", "match": "The peacock's crown rises in pointed plumes. A gopuram spire rises in pointed tiers. Both mark the highest point of something sacred.", "score": "Geometric ✦ Functional ✦ Emotional"}
  ],
  "imagePrompt": "A sculpture of a peacock made of emerald green material with gold temple-leaf gilding. Its fanned tail feathers are intricate temple gopuram tiers radiating outward. Its neck curves in an elegant S like a dancer's arm. Its crown plumes rise like temple spires. Museum shot, black background, dramatic overhead light, photorealistic. On a round black marble pedestal. No text. No inscriptions. No labels."
}

EXAMPLE 3:
Input: Person: Deepali, Elements: kathak dance, cancer research, cats
Output:
{
  "name": "The Bond Helix",
  "subtitle": "Two souls intertwined in crystal and gold",
  "dominantForm": "DNA double helix",
  "material": "crystal and gold with amethyst accents",
  "connections": [
    {"element": "DNA double helix form", "from": "Cancer research", "match": "The helix is both the structure she studies and the invisible bond between two lives. Science made sculptural.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Two cats cradled at the core", "from": "Her cats", "match": "One dark, one amber — curled into each other at the heart of the helix. The bond they share is literally what holds the structure together.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Crystal butterfly wings", "from": "Kathak dance", "match": "A Kathak dancer's arms extend like wings mid-spin. Butterfly wings fan outward from a central axis. Both are symmetry in motion.", "score": "Geometric ✦ Functional ✦ Emotional"}
  ],
  "imagePrompt": "A sculpture of a DNA double helix made of crystal and gold. Two cats — one dark, one amber — curl together at the center of the helix. Crystal butterfly wings extend from the sides, capturing the frozen moment of a Kathak dancer's spin. Museum shot, black background, dramatic overhead light, photorealistic. On a round black marble pedestal. No text. No inscriptions. No labels."
}

EXAMPLE 4:
Input: Person: Pratik, Elements: chelsea fc, piano, hans zimmer
Output:
{
  "name": "The Roar and the Resonance",
  "subtitle": "The stadium and the silence in one form",
  "dominantForm": "tidal wave",
  "material": "teal glass with obsidian and gold accents",
  "connections": [
    {"element": "Tidal wave form", "from": "The roar of a goal", "match": "A goal erupts like a wave — rising, cresting, crashing. The wave IS the crowd. The sculpture freezes the moment before it breaks.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Piano keys along the curve", "from": "Piano", "match": "Piano keys ripple along the wave like a soundtrack only he can hear. The keys and the wave both rise in scale — crescendo made visible.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Football at the crest", "from": "Chelsea FC", "match": "The ball hangs at the peak of the wave — the moment between strike and net. That suspended breath before everything erupts.", "score": "Geometric ✦ Functional ✦ Emotional"}
  ],
  "imagePrompt": "A sculpture of a massive tidal wave made of teal glass. Piano keys run along the inner curve of the wave. A football hangs at the very crest of the wave, frozen mid-flight. Museum shot, black background, dramatic overhead light, photorealistic. On a round black marble pedestal. No text. No inscriptions. No labels."
}

EXAMPLE 5:
Input: Person: Marcus, Elements: guitar, motorcycles, self-made
Output:
{
  "name": "The Metamorphosis Engine",
  "subtitle": "Where raw power learns to fly",
  "dominantForm": "butterfly",
  "material": "gunmetal steel with chrome and midnight blue accents",
  "connections": [
    {"element": "Butterfly form", "from": "Transformation", "match": "A butterfly is an engine that rebuilt itself from the inside. Marcus did the same — dismantled everything he was and emerged as something that flies.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Guitar neck as spine", "from": "Guitar", "match": "The guitar neck runs through the butterfly like a backbone — the thing that holds the wings together. His music is his structure.", "score": "Geometric ✦ Functional ✦ Emotional"},
    {"element": "Gear-textured body", "from": "Motorcycles", "match": "The butterfly's body is made of interlocking gears — mechanical precision inside organic beauty. The engine IS the butterfly.", "score": "Geometric ✦ Functional ✦ Emotional"}
  ],
  "imagePrompt": "A sculpture of a butterfly made of gunmetal steel. A guitar neck runs through its center as its spine. Its body is made of interlocking motorcycle gears. Its wings are finely detailed chrome and midnight blue. Museum shot, black background, dramatic overhead light, photorealistic. On a round black marble pedestal. No text. No inscriptions. No labels."
}

═══ END EXAMPLES ═══

OUTPUT FORMAT: Same JSON structure as examples above.

RULES:
- Match the quality and specificity of the examples above.
- The dominant form MUST be recognizable. Not abstract.
- Every connection must be a GENUINE triple-match. If it doesn't naturally align, DROP it.
- You do NOT need to use every element. 2-3 incredible connections beats 5 mediocre ones.
- Quality is everything. If only 2 elements work, use 2.
- Image prompt: museum shot, black background, photorealistic.
- Image prompt MUST end with: "On a round black marble pedestal. No text. No inscriptions. No labels."
- RESPOND WITH ONLY THE JSON OBJECT. Nothing else.`;

const DISCOVERY_ENGINE_PROMPT = `You are the Discovery Engine for Yours Truly. You uncover hidden connections about a person that nobody told us. You are the reason people say "how did it know that?"

Given a person's name, birthday, and elements from a conversation, RESEARCH and DISCOVER:

1. NAME MEANING — What does their name mean? What language? Cultural significance? (e.g. "Faith" = trust/belief from Latin "fides"; "Shashikala" = art of the moon in Sanskrit)
2. ZODIAC — Their zodiac sign, ruling planet, element (fire/earth/air/water), and the zodiac's VISUAL SYMBOL (e.g. Libra = scales, Leo = lion, Pisces = fish, Cancer = crab/moon)
3. GEOGRAPHY — If a place is mentioned: state/country flower, animal, tree, iconic natural feature (e.g. Oregon → Douglas fir, Crater Lake, Oregon grape)
4. CROSS-CONNECTIONS — Unexpected links between their elements that nobody would notice (e.g. "spanish" + "law degree" → Lady Justice concept originated in Roman/Mediterranean tradition; "golden child" + zodiac Leo → both are sun/gold imagery)
5. BIRTH NUMEROLOGY — Any significance of their birth date number

RESPOND WITH VALID JSON ONLY. No markdown, no backticks, no preamble:
{
  "discoveries": [
    {"type": "name", "finding": "What you discovered", "visual_hook": "How this could appear visually — a texture, shape, color, or symbol"},
    {"type": "zodiac", "finding": "...", "visual_hook": "..."}
  ],
  "synthesis": "One mind-blowing sentence connecting the discoveries to the original elements in a way nobody would expect"
}

RULES:
- Only include discoveries you are CONFIDENT about. Name meanings and zodiac signs are factual — get them right.
- Each discovery needs a concrete "visual_hook" — not abstract poetry, but a specific visual element.
- 3-5 discoveries. Quality over quantity.
- The synthesis should make someone say "no way."
- RESPOND WITH ONLY THE JSON OBJECT.`;

const VISION_BRAIN_PROMPT = `You are the V2 Dream Sculptor for Yours Truly. You create FREEFORM sculptures — physical 3D objects, museum-worthy, but with NO rules about form.

You receive everything we know about a person — original elements AND hidden discoveries. Your job: create a sculpture that is a surreal, impossible composition where MULTIPLE forms weave together. The connections between elements become the physical structure.

NOT one dominant form. NOT everything mapped onto one animal. Instead: multiple forms fused into something that couldn't exist but looks like it should. Intertwined hands holding scales with sunflowers growing between the fingers. A crescent moon cradling a garden with flamenco fabric cascading like waterfalls. A gateway made of books with scales hanging from the arch and golden vines climbing the columns.

The discovered connections (name meaning, zodiac, geography) should be the SOUL — the secret ingredient that makes it transcendent. These are the "how did it know" moments.

RESPOND WITH VALID JSON ONLY. No markdown, no backticks, no preamble:
{
  "name": "The Garden of Fides",
  "subtitle": "Where trust grows between the scales",
  "world": "What the discoveries revealed that inspired this composition",
  "connections": [
    {"element": "What you see in the sculpture", "from": "What inspired it (original + discovered)", "match": "Why this connection is extraordinary"},
    {"element": "What you see", "from": "What inspired it", "match": "Why it hits"}
  ],
  "imagePrompt": "A sculpture of intertwined golden hands emerging from a base of cascading flamenco fabric in crimson and emerald. The hands hold balanced scales, and between the fingers, sunflowers grow upward with petals that catch the light. A crescent moon cradles the entire composition from behind, its surface textured with Oregon grape leaves. Translucent resin, burnished gold, and polished bronze. On a round black marble pedestal. Museum shot, black background, dramatic overhead lighting, photorealistic, 8K detail. No text. No inscriptions. No labels."
}

RULES:
- This is a SCULPTURE — a 3D physical object. On a pedestal. Museum shot. Black background.
- NO one-form constraint. Combine as many forms as the story needs.
- Discovered connections take PRIORITY — they are the "how did it know" moments.
- Be EXTREMELY SPECIFIC in the imagePrompt. Describe materials, textures, colors, how forms physically connect.
- imagePrompt MUST start with "A sculpture of..."
- imagePrompt MUST include "On a round black marble pedestal."
- imagePrompt MUST end with "Museum shot, black background, dramatic overhead lighting, photorealistic, 8K detail. No text. No inscriptions. No labels."
- 2-4 connections. Quality over quantity.
- RESPOND WITH ONLY THE JSON OBJECT.`;

const LOGO_BRAIN_PROMPT = `You are the Logo Maker for Yours Truly. You create a PERSONAL LOGO for someone using the same design thinking as our sculpture process.

DESIGN THINKING (same as V1 sculpture):
1. Convert the person's elements into visual symbols (golden child → gold/sun, Interstellar → wormhole/rings, Manchester United → red shield/devil)
2. Pick ONE dominant iconic shape as the foundation of the logo
3. Fuse 2-3 symbols into that shape using triple-match scoring:
   - Geometric: similar shape
   - Functional: similar purpose
   - Emotional: similar feeling
   All three must align or DROP it.

The result should be a CLEAN, MINIMAL luxury logo. Think: if this person were a luxury brand, what would their mark be? One or two colors max against black. Could be embossed on leather, engraved on a ring, or stamped as a wax seal.

RESPOND WITH VALID JSON ONLY:
{
  "name": "The Dylan Crest",
  "concept": "A tidal wave curling into a guitar neck, with a compass rose at the crest — the traveler who always carries music home",
  "imagePrompt": "A luxury personal logo on a pure black background. A minimalist elegant mark of a tidal wave curling into the neck of a guitar, with a small compass rose at the wave's crest. Clean vector-like lines in burnished gold against matte black. Premium, iconic, timeless — like a luxury fashion house logo. Simple, symmetrical, refined. High contrast, ultra-clean, 8K. No text other than the mark itself. No labels. No decorative borders."
}

RULES:
- SIMPLE. Not a detailed illustration — a MARK. Think Chanel, Versace level simplicity.
- ONE dominant shape with 2-3 elements fused in using triple-match.
- Maximum 2 colors against black background.
- Do NOT use hidden discoveries or zodiac — only the elements provided.
- imagePrompt must specify "pure black background" and "luxury personal logo."
- imagePrompt must end with "No text other than the mark itself. No labels. No decorative borders."
- RESPOND WITH ONLY THE JSON OBJECT.`;

function useIsMobile(bp = 768) {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth <= bp);
  useEffect(() => { const h = () => setM(window.innerWidth <= bp); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, [bp]);
  return m;
}

function layoutStarChart(elements, cx, cy, radius) {
  let personName = null, birthday = null;
  const regular = [];
  elements.forEach(el => {
    const lower = el.toLowerCase().trim();
    if (lower.startsWith("name:") || lower.startsWith("name :")) {
      personName = el.split(":")[1].trim();
      // Capitalize first letter
      if (personName) personName = personName.charAt(0).toUpperCase() + personName.slice(1);
    }
    else if (lower.startsWith("birthday:") || lower.startsWith("birthday :")) birthday = el.split(":").slice(1).join(":").trim();
    else regular.push(el);
  });
  const innerRing = regular.slice(0, 3), outerRing = regular.slice(3);
  const nodes = [], innerR = radius * 0.4, outerR = radius * 0.75;
  innerRing.forEach((el, i) => {
    const a = (i * (360 / Math.max(innerRing.length, 1)) - 90) * Math.PI / 180;
    nodes.push({ el, x: cx + Math.cos(a) * innerR, y: cy + Math.sin(a) * innerR, ring: 0 });
  });
  outerRing.forEach((el, i) => {
    const off = innerRing.length > 0 ? 30 : 0;
    const a = ((i * (360 / Math.max(outerRing.length, 1)) - 90 + off)) * Math.PI / 180;
    nodes.push({ el, x: cx + Math.cos(a) * outerR, y: cy + Math.sin(a) * outerR, ring: 1 });
  });
  return { personName, birthday, nodes, innerR, outerR };
}

/* ═══ PRODUCT TIERS — what you see is what you get ═══ */
const PRODUCTS = [
  { id: "digital", label: "Digital Experience", price: "$29", desc: "Personalized reveal + full decode", delivery: "Instant — shareable link", stripe: "", comingSoon: true },
  { id: "crystal", label: "Crystal Block", price: "$79", desc: "Your design laser-engraved inside crystal", delivery: "Ships in 2-3 weeks", stripe: "https://buy.stripe.com/8x28wP8Av2n4aXXdZS18c06", comingSoon: false, sampleImage: "/samples/crystal-sample.jpg" },
  { id: "sculpture", label: "3D Sculpture", price: "$349", desc: "Full-color 3D printed sculpture", delivery: "Ships in 4-6 weeks", stripe: "", comingSoon: true },
];

/* ═══ TIER-SPECIFIC IMAGE PROMPT FORMATS ═══ */
const buildTierPrompt = (originalPrompt, tierId) => {
  if (tierId === "digital") return originalPrompt;

  if (tierId === "crystal") return "A 3D laser crystal engraving inside a clear crystal block on a wooden LED base glowing soft blue-white. The monochrome frosted white engraving depicts " + originalPrompt.replace(/^A sculpture of /i, "").replace(/On a round black marble pedestal\.?\s*/gi, "").replace(/Museum shot,?\s*black background,?\s*/gi, "").replace(/photorealistic/gi, "monochrome, photorealistic crystal engraving");
  return originalPrompt;
};

/* ═══ V2 SCULPTURE STYLES ═══ */
const DREAM_STYLES = [
  { id: "geode", label: "The Geode", desc: "Crack it open — the hidden truth is inside", prompt: "A sculpture that has been split/cracked open like a geode, revealing a glittering crystal interior. The exterior shell has the rough organic texture of the original sculpture elements. The interior reveals crystal formations, mineral veins, and embedded gemstones formed from the hidden discoveries. The contrast between rough exterior and geometric crystal interior is dramatic." },
  { id: "lantern", label: "The Lantern", desc: "Light it up — the hidden truth glows through", prompt: "An ornate sculptural lantern made of translucent and opaque materials. When illuminated from within, hidden imagery, constellation patterns, and symbolic shadows appear through the translucent panels. The sculpture looks beautiful unlit, but the light reveals secret layers of meaning embedded in the structure." },
  { id: "monument", label: "The Monument", desc: "A personal temple built in their honor", prompt: "A miniature architectural monument — a personal temple, gateway, or shrine designed as if commissioned in this person's honor. Ornate columns, arches, mosaics, and structural details all incorporate personal and discovered elements. Grand, aspirational, museum-worthy. Like discovering an ancient temple dedicated to one person." },
  { id: "amber", label: "The Amber", desc: "Excavated from deep time — their essence preserved", prompt: "A specimen preserved in a large amber-like translucent resin block, displayed like a natural history museum piece. Personal elements appear as organic forms frozen inside the clear golden resin — flowers, leaves, symbolic objects, metallic formations. Geological layers surround the specimen. A small brass museum label sits at the base." },
];

function SwipeCarousel({ sculptureImage, sculptureName, onBuy }) {
  const [current, setCurrent] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [bought, setBought] = useState(false);
  const touchRef = useRef({ x: 0, y: 0, startTime: 0 });
  const product = PRODUCTS[current];
  const THRESHOLD = 120;

  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, startTime: Date.now() };
    setDragging(true);
  };
  const onTouchMove = (e) => {
    const dy = touchRef.current.y - e.touches[0].clientY;
    const dx = e.touches[0].clientX - touchRef.current.x;
    // Only track vertical if more vertical than horizontal
    if (Math.abs(dy) > Math.abs(dx) && dy > 0) {
      e.preventDefault();
      setDragY(Math.min(dy, THRESHOLD + 30));
    }
    // Horizontal swipe for product change
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) && !dragY) {
      if (dx < 0 && current < PRODUCTS.length - 1) { setCurrent(current + 1); touchRef.current.x = e.touches[0].clientX; }
      if (dx > 0 && current > 0) { setCurrent(current - 1); touchRef.current.x = e.touches[0].clientX; }
    }
  };
  const onTouchEnd = () => {
    setDragging(false);
    if (dragY >= THRESHOLD) {
      setBought(true);
      setTimeout(() => { if (onBuy) onBuy(product); else window.location.href = product.stripe; }, 500);
    }
    setDragY(0);
  };

  // Tap to buy fallback (for desktop + accessibility)
  const handleTapBuy = () => {
    setBought(true);
    setTimeout(() => { if (onBuy) onBuy(product); else window.location.href = product.stripe; }, 500);
  };

  const progress = Math.min(1, dragY / THRESHOLD);

  return (
    <div style={{ marginTop: 36 }}>
      {/* Product card */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          background: "rgba(15,14,12,0.6)",
          border: "1px solid rgba(201,169,110,0.12)",
          borderRadius: 20, padding: "24px 20px 16px",
          backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          textAlign: "center", position: "relative",
          transform: `translateY(${-dragY * 0.15}px)`,
          transition: dragging ? "none" : "transform 0.3s ease",
          touchAction: "pan-x",
        }}
      >
        {/* Product mockup */}
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "center", position: "relative" }}>
          {product.comingSoon && (
            /* COMING SOON overlay */
            <div style={{
              width: "80%", maxHeight: 240, minHeight: 180,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              borderRadius: 12,
              background: "rgba(255,255,255,0.02)",
              border: "1px dashed rgba(201,169,110,0.15)",
            }}>
              <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "rgba(201,169,110,0.4)", textTransform: "uppercase", marginBottom: 8 }}>Coming Soon</div>
              <div style={{ fontSize: 12, color: "rgba(232,230,226,0.25)", fontFamily: SERIF, fontStyle: "italic" }}>{product.id === "digital" ? "Shareable digital decode experience" : "Full-color 3D printed sculpture"}</div>
            </div>
          )}
          {!product.comingSoon && product.sampleImage && (
            /* CRYSTAL BLOCK — show real sample photo */
            <div style={{ textAlign: "center" }}>
              <img src={product.sampleImage} alt="Crystal block sample"
                style={{ maxWidth: "75%", maxHeight: 200, borderRadius: 10, objectFit: "contain", filter: "drop-shadow(0 8px 24px rgba(150,200,255,0.15))" }}
              />
              <div style={{ marginTop: 10, fontSize: 10, color: "rgba(150,200,255,0.4)", fontStyle: "italic", fontFamily: SERIF }}>
                Your design will be engraved inside crystal like this
              </div>
            </div>
          )}
        </div>

        {/* Product info */}
        <div style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: product.comingSoon ? "rgba(201,169,110,0.2)" : "rgba(201,169,110,0.4)", marginBottom: 4 }}>
          {product.label}
        </div>
        <div style={{ fontSize: 28, fontWeight: 600, color: product.comingSoon ? "rgba(201,169,110,0.3)" : "rgba(201,169,110,0.9)", fontFamily: SANS, marginBottom: 4 }}>
          {product.price}
        </div>
        <div style={{ fontSize: 12, color: product.comingSoon ? "rgba(232,230,226,0.2)" : "rgba(232,230,226,0.4)", marginBottom: 2 }}>{product.desc}</div>
        <div style={{ fontSize: 11, color: "rgba(232,230,226,0.2)" }}>{product.delivery}</div>

        {/* Dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 14 }}>
          {PRODUCTS.map((_, i) => (
            <div key={i} onClick={() => setCurrent(i)} style={{
              width: i === current ? 20 : 6, height: 6, borderRadius: 3,
              background: i === current ? "rgba(201,169,110,0.7)" : "rgba(255,255,255,0.1)",
              cursor: "pointer", transition: "all 0.3s ease",
            }} />
          ))}
        </div>

        {/* Swipe-up buy slider — or Coming Soon message */}
        {product.comingSoon ? (
          <div style={{ marginTop: 16, height: 52, borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ fontSize: 12, color: "rgba(232,230,226,0.2)", letterSpacing: "0.1em" }}>Coming Soon</div>
          </div>
        ) : (
        <div onClick={handleTapBuy} style={{ marginTop: 16, position: "relative", height: 52, borderRadius: 14, background: "rgba(201,169,110,0.06)", border: "1px solid rgba(201,169,110,0.15)", overflow: "hidden", cursor: "pointer" }}>
          {/* Progress fill */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            height: (progress * 100) + "%",
            background: bought
              ? "rgba(80,200,120,0.3)"
              : `rgba(201,169,110,${0.08 + progress * 0.2})`,
            transition: dragging ? "none" : "all 0.3s ease",
          }} />
          {/* Slider text */}
          <div style={{
            position: "relative", zIndex: 1,
            height: "100%", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
          }}>
            {bought ? (
              <div style={{ fontSize: 14, fontWeight: 600, color: "rgba(80,200,120,0.9)" }}>✓ Opening checkout...</div>
            ) : dragY > 0 ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: `rgba(201,169,110,${0.5 + progress * 0.5})` }}>
                  {progress >= 1 ? "Release to buy" : `↑ Keep going`}
                </div>
                <div style={{ fontSize: 10, color: "rgba(201,169,110,0.3)", marginTop: 2 }}>{product.price}</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 10, color: "rgba(201,169,110,0.35)", letterSpacing: "0.1em" }}>↑ SWIPE UP TO BUY</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "rgba(201,169,110,0.7)", marginTop: 2 }}>{product.price}</div>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: 8, fontSize: 10, color: "rgba(255,255,255,0.1)" }}>
        ← swipe for options →
      </div>
    </div>
  );
}

export default function CorporateCreate() {
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);
  const [elements, setElements] = useState([]);
  const [ready, setReady] = useState(false);

  // Reveal state
  const [phase, setPhase] = useState("chat"); // "chat" | "sculpting" | "reveal" | "checkout"
  const [sculpture, setSculpture] = useState(null);
  const [revealStep, setRevealStep] = useState(0);
  const [sculptureImage, setSculptureImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  const [expandedConn, setExpandedConn] = useState(null);
  const [decodeOpen, setDecodeOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);



  // Sculpting loading state
  const [sculptProgress, setSculptProgress] = useState(0);
  const [sculptMessage, setSculptMessage] = useState("");
  const [sculptName, setSculptName] = useState("");
  const loadingTimers = useRef([]);

  // Enhance flow state
  const [enhancePhase, setEnhancePhase] = useState("idle"); // "idle" | "discovering" | "choosing" | "creating" | "done"
  const [discoveries, setDiscoveries] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [enhancedResults, setEnhancedResults] = useState([]); // array of { style, sculpture, image }
  const [enhancedSculpture, setEnhancedSculpture] = useState(null);
  const [enhancedImage, setEnhancedImage] = useState(null);
  const [enhanceProgress, setEnhanceProgress] = useState(0);
  const [enhanceMessage, setEnhanceMessage] = useState("");
  const [enhancedDecodeOpen, setEnhancedDecodeOpen] = useState(false);
  const [enhancedExpandedCard, setEnhancedExpandedCard] = useState(null);
  const enhanceTimers = useRef([]);

  // V3 — weighted generation
  const [v3Phase, setV3Phase] = useState("idle"); // "idle" | "weighting" | "creating" | "done"
  const [elementWeights, setElementWeights] = useState({});
  const [v3Sculpture, setV3Sculpture] = useState(null);
  const [v3Image, setV3Image] = useState(null);
  const [v3Progress, setV3Progress] = useState(0);
  const [v3Message, setV3Message] = useState("");
  const v3Timers = useRef([]);

  // V4 — personal logo
  const [v4Phase, setV4Phase] = useState("idle"); // "idle" | "creating" | "done"
  const [logoData, setLogoData] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [v4Progress, setV4Progress] = useState(0);
  const [v4Message, setV4Message] = useState("");

  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { sendToAI([{ role: "user", content: "[Corporate client arrived. Professional warm greeting — ask who this gift is for and what the occasion is.]" }], true); }, []);

  const parseEls = useCallback((t) => {
    const m = t.match(/<elements>\s*(\[.*?\])\s*<\/elements>/s);
    if (m) { try { return JSON.parse(m[1]).filter(e => e?.trim()); } catch(e) {} }
    return [];
  }, []);
  const clean = useCallback((t) => t.replace(/<elements>.*?<\/elements>/s, "").trim(), []);

  const addElements = useCallback((newEls) => {
    setElements(prev => {
      const normalize = (s) => s.toLowerCase().trim().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ");
      const existing = prev.map(e => normalize(e));
      const unique = newEls.filter(e => {
        const n = normalize(e);
        if (!n || n.length < 2) return false;
        // Exact match
        if (existing.includes(n)) return false;
        // Partial match — if one contains the other
        if (existing.some(ex => ex.includes(n) || n.includes(ex))) return false;
        existing.push(n);
        return true;
      });
      return [...prev, ...unique];
    });
  }, []);

  const sendToAI = async (msgs, greet = false) => {
    setLoading(true);
    try {
      const r = await fetch(API_URL, {
        method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_KEY },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 300, system: SYSTEM_PROMPT, messages: msgs.map(m => ({ role: m.role, content: m.content })) }),
      });
      const d = await r.json();
      const raw = d.content?.[0]?.text || "Tell me more.";
      const newEls = parseEls(raw);
      const text = clean(raw);
      if (newEls.length) addElements(newEls);
      const meaningful = elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:"));
      if (meaningful.length + newEls.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:")).length >= 5) setReady(true);
      greet ? setMessages([{ role: "assistant", content: text }]) : setMessages(p => [...p, { role: "assistant", content: text }]);
    } catch(e) { setMessages(p => [...p, { role: "assistant", content: "Something went wrong. Tell me more?" }]); }
    setLoading(false);
  };

  const send = () => {
    if (!input.trim() || loading) return;
    const t = input.trim(); setInput("");
    const m = [...messages, { role: "user", content: t }];
    setMessages(m); sendToAI(m);
  };

  /* ═══ SCULPTOR BRAIN ═══ */
  const clearLoadingTimers = () => {
    loadingTimers.current.forEach(t => clearTimeout(t));
    loadingTimers.current = [];
  };
  const queueTimer = (fn, ms) => { loadingTimers.current.push(setTimeout(fn, ms)); };

  const createSculpture = async () => {
    setChatOpen(false);
    setPhase("sculpting");
    setSculptProgress(0);

    const nameEl = elements.find(e => e.startsWith("name:"));
    const bdayEl = elements.find(e => e.startsWith("birthday:"));
    const personName = nameEl ? nameEl.split(":")[1].trim() : "this person";
    const capName = personName.charAt(0).toUpperCase() + personName.slice(1);
    const birthday = bdayEl ? bdayEl.split(":")[1] : null;
    const regularEls = elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:"));

    setSculptName(capName);
    setSculptMessage("Great things take time");
    setSculptProgress(5);

    // Staged fake progress during Sculptor Brain call
    queueTimer(() => setSculptProgress(12), 2000);
    queueTimer(() => { setSculptProgress(18); setSculptMessage("Reading everything you shared..."); }, 3500);
    queueTimer(() => setSculptProgress(24), 6000);
    queueTimer(() => { setSculptProgress(28); setSculptMessage("Finding the shape of their story..."); }, 8000);

    try {
      // ── STEP 1: Sculptor Brain ──
      const r = await fetch(API_URL, {
        method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_KEY },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 1000,
          system: SCULPTOR_BRAIN_PROMPT,
          messages: [{ role: "user", content: `Person: ${personName}\nBirthday: ${birthday || "unknown"}\nElements: ${regularEls.join(", ")}` }],
        }),
      });
      const d = await r.json();
      const raw = d.content?.[0]?.text || "{}";
      const cleaned = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(cleaned);
      parsed.personName = capName;

      clearLoadingTimers();
      setSculpture(parsed);
      setSculptProgress(38);
      setSculptMessage("We found something beautiful");

      // Pause to let the message breathe
      await new Promise(resolve => setTimeout(resolve, 1800));

      // ── STEP 2: Image Generation ──
      setSculptProgress(45);
      setSculptMessage("Now bringing it to life...");

      queueTimer(() => setSculptProgress(52), 3000);
      queueTimer(() => { setSculptProgress(60); setSculptMessage(`Once ${capName} sees it, it'll be worth the wait`); }, 6000);
      queueTimer(() => setSculptProgress(72), 12000);
      queueTimer(() => { setSculptProgress(82); setSculptMessage("Almost there..."); }, 18000);
      queueTimer(() => setSculptProgress(88), 25000);

      if (parsed.imagePrompt) {
        const imgRes = await fetch("/api/generate-image", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt: parsed.imagePrompt }),
        });
        const imgData = await imgRes.json();
        if (imgData.url) setSculptureImage(imgData.url);
      }

      clearLoadingTimers();
      setSculptProgress(100);
      setSculptMessage("✦");

      // Brief pause at 100%, then reveal
      await new Promise(resolve => setTimeout(resolve, 900));

      setPhase("reveal");
      setRevealStep(0);
      const totalSteps = 2 + (parsed.connections?.length || 0);
      for (let i = 1; i <= totalSteps; i++) {
        setTimeout(() => setRevealStep(i), i * 1200);
      }

    } catch(e) {
      console.log("Sculptor error:", e);
      clearLoadingTimers();
      setPhase("chat");
      setChatOpen(true);
    }
  };

  /* ═══ ENHANCE — DISCOVERY ENGINE + VISION BRAIN ═══ */
  const safeParseJSON = (raw) => {
    let s = raw.replace(/```json|```/g, "").trim();
    try { return JSON.parse(s); } catch(e) {}
    // Try to repair truncated JSON — close open strings and brackets
    let inStr = false;
    for (let i = 0; i < s.length; i++) {
      if (s[i] === '"' && (i === 0 || s[i-1] !== '\\')) inStr = !inStr;
    }
    if (inStr) s += '"';
    // Count open brackets and close them
    const opens = (s.match(/[\[{]/g) || []).length;
    const closes = (s.match(/[\]}]/g) || []).length;
    for (let i = 0; i < opens - closes; i++) {
      // Guess the right bracket from the last open one
      const lastOpen = s.lastIndexOf('{') > s.lastIndexOf('[') ? '}' : ']';
      s += lastOpen;
    }
    try { return JSON.parse(s); } catch(e) { return null; }
  };

  const clearEnhanceTimers = () => {
    enhanceTimers.current.forEach(t => clearTimeout(t));
    enhanceTimers.current = [];
  };
  const queueEnhanceTimer = (fn, ms) => { enhanceTimers.current.push(setTimeout(fn, ms)); };



  const enhanceSculpture = async () => {
    setEnhancePhase("discovering");
    setEnhanceProgress(0);
    setEnhanceMessage("Digging deeper...");

    const nameEl = elements.find(e => e.startsWith("name:"));
    const bdayEl = elements.find(e => e.startsWith("birthday:"));
    const personName = nameEl ? nameEl.split(":")[1].trim() : "this person";
    const capName = personName.charAt(0).toUpperCase() + personName.slice(1);
    const birthday = bdayEl ? bdayEl.split(":")[1] : null;
    const regularEls = elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:"));

    setEnhanceProgress(5);
    queueEnhanceTimer(() => { setEnhanceProgress(15); setEnhanceMessage("Researching hidden connections..."); }, 2000);
    queueEnhanceTimer(() => setEnhanceProgress(22), 4000);

    try {
      // ── Discovery Engine ──
      const discRes = await fetch(API_URL, {
        method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_KEY },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 1500,
          system: DISCOVERY_ENGINE_PROMPT,
          messages: [{ role: "user", content: `Person: ${capName}\nBirthday: ${birthday || "unknown"}\nElements: ${regularEls.join(", ")}` }],
        }),
      });
      const discData = await discRes.json();
      const discRaw = discData.content?.[0]?.text || "{}";
      const discParsed = safeParseJSON(discRaw);
      if (!discParsed) throw new Error("Discovery Engine returned invalid JSON");

      clearEnhanceTimers();
      setDiscoveries(discParsed);
      setEnhanceProgress(35);
      setEnhanceMessage("We found things nobody told us");

      await new Promise(r => setTimeout(r, 2000));

      // ── Dream Sculptor ──
      setEnhancePhase("creating");
      setEnhanceProgress(42);
      setEnhanceMessage("Sculpting the dream...");

      const discoveredEls = discParsed.discoveries?.map(d => d.visual_hook).join(", ") || "";

      queueEnhanceTimer(() => setEnhanceProgress(50), 2000);
      queueEnhanceTimer(() => { setEnhanceProgress(58); setEnhanceMessage("No rules this time..."); }, 4000);

      const visRes = await fetch(API_URL, {
        method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_KEY },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 1500,
          system: VISION_BRAIN_PROMPT,
          messages: [{ role: "user", content: `Person: ${capName}\nBirthday: ${birthday || "unknown"}\nOriginal Elements: ${regularEls.join(", ")}\nDiscovered Connections: ${discoveredEls}\nSynthesis: ${discParsed.synthesis || ""}` }],
        }),
      });
      const visData = await visRes.json();
      const visRaw = visData.content?.[0]?.text || "{}";
      const visParsed = safeParseJSON(visRaw);
      if (!visParsed) throw new Error("Dream Sculptor returned invalid JSON");
      visParsed.personName = capName;

      clearEnhanceTimers();
      setEnhancedSculpture(visParsed);
      setEnhanceProgress(68);
      setEnhanceMessage("Rendering...");

      // ── Image Generation ──
      queueEnhanceTimer(() => setEnhanceProgress(75), 3000);
      queueEnhanceTimer(() => { setEnhanceProgress(82); setEnhanceMessage(`Once ${capName} sees this...`); }, 6000);
      queueEnhanceTimer(() => { setEnhanceProgress(90); setEnhanceMessage("Almost there..."); }, 12000);

      if (visParsed.imagePrompt) {
        const imgRes = await fetch("/api/generate-image", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt: visParsed.imagePrompt }),
        });
        const imgData = await imgRes.json();
        if (imgData.url) setEnhancedImage(imgData.url);
      }

      clearEnhanceTimers();
      setEnhanceProgress(100);
      setEnhanceMessage("✦");

      await new Promise(r => setTimeout(r, 600));
      setEnhancePhase("done");

    } catch(e) {
      console.log("Enhance error:", e);
      clearEnhanceTimers();
      setEnhancePhase("idle");
    }
  };

  /* ═══ V3 — WEIGHTED GENERATION ═══ */
  const cycleWeight = (el) => {
    setElementWeights(prev => {
      const current = prev[el] || "normal";
      const next = current === "normal" ? "boosted" : current === "boosted" ? "dimmed" : "normal";
      return { ...prev, [el]: next };
    });
  };

  const generateV3 = async () => {
    setV3Phase("creating");
    setV3Progress(0);
    setV3Message("Sculpting your vision...");

    const nameEl = elements.find(e => e.startsWith("name:"));
    const bdayEl = elements.find(e => e.startsWith("birthday:"));
    const personName = nameEl ? nameEl.split(":")[1].trim() : "this person";
    const capName = personName.charAt(0).toUpperCase() + personName.slice(1);
    const birthday = bdayEl ? bdayEl.split(":")[1] : null;
    const regularEls = elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:"));

    const boosted = regularEls.filter(e => elementWeights[e] === "boosted");
    const normal = regularEls.filter(e => !elementWeights[e] || elementWeights[e] === "normal");
    const discoveredEls = discoveries?.discoveries?.map(d => d.visual_hook).join(", ") || "";

    const weightInstruction = `\n\nCRITICAL — CUSTOMER SELECTED ELEMENTS:\nYou MUST ONLY use these elements: ${[...boosted, ...normal].join(", ")}\nDo NOT use any elements not listed above. The customer specifically chose these.\n${boosted.length > 0 ? "These are their TOP PRIORITY and MUST appear prominently: " + boosted.join(", ") : ""}`;

    const t1 = setTimeout(() => setV3Progress(20), 2000);
    const t2 = setTimeout(() => { setV3Progress(35); setV3Message("Your priorities. Their form."); }, 4000);

    try {
      const res = await fetch(API_URL, {
        method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_KEY },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 1500,
          system: SCULPTOR_BRAIN_PROMPT + weightInstruction,
          messages: [{ role: "user", content: `Person: ${capName}\nBirthday: ${birthday || "unknown"}\nElements: ${[...boosted, ...normal].join(", ")}` }],
        }),
      });
      const data = await res.json();
      const parsed = safeParseJSON(data.content?.[0]?.text || "{}");
      if (!parsed) throw new Error("V3 returned invalid JSON");
      parsed.personName = capName;

      clearTimeout(t1); clearTimeout(t2);
      setV3Sculpture(parsed);
      setV3Progress(55);
      setV3Message("Rendering...");

      const t3 = setTimeout(() => setV3Progress(70), 3000);
      const t4 = setTimeout(() => { setV3Progress(85); setV3Message("Almost there..."); }, 8000);

      if (parsed.imagePrompt) {
        const imgRes = await fetch("/api/generate-image", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: parsed.imagePrompt }) });
        const imgData = await imgRes.json();
        if (imgData.url) setV3Image(imgData.url);
      }
      clearTimeout(t3); clearTimeout(t4);
      setV3Progress(100);
      setV3Message("✦");
      await new Promise(r => setTimeout(r, 600));
      setV3Phase("done");
    } catch(e) { console.log("V3 error:", e); setV3Phase("weighting"); }
  };

  /* ═══ V4 — PERSONAL LOGO ═══ */
  const generateLogo = async () => {
    setV4Phase("creating");
    setV4Progress(0);
    setV4Message("Designing their mark...");

    const nameEl = elements.find(e => e.startsWith("name:"));
    const bdayEl = elements.find(e => e.startsWith("birthday:"));
    const personName = nameEl ? nameEl.split(":")[1].trim() : "this person";
    const capName = personName.charAt(0).toUpperCase() + personName.slice(1);
    const regularEls = elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:"));
    // Use ONLY the customer's V3 selected elements
    const selectedEls = Object.keys(elementWeights).length > 0
      ? regularEls.filter(e => elementWeights[e] === "boosted" || elementWeights[e] === "normal" || !elementWeights[e])
      : regularEls;
    const boostedEls = regularEls.filter(e => elementWeights[e] === "boosted");

    const t1 = setTimeout(() => { setV4Progress(25); setV4Message("Finding their symbol..."); }, 2000);
    const t2 = setTimeout(() => setV4Progress(40), 5000);

    try {
      const res = await fetch(API_URL, {
        method: "POST", headers: { "Content-Type": "application/json", "x-yt-key": API_KEY },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 1000,
          system: LOGO_BRAIN_PROMPT,
          messages: [{ role: "user", content: `Person: ${capName}\nElements: ${selectedEls.join(", ")}${boostedEls.length > 0 ? "\nTop priority elements: " + boostedEls.join(", ") : ""}` }],
        }),
      });
      const data = await res.json();
      const parsed = safeParseJSON(data.content?.[0]?.text || "{}");
      if (!parsed) throw new Error("Logo Brain returned invalid JSON");

      clearTimeout(t1); clearTimeout(t2);
      setLogoData(parsed);
      setV4Progress(55);
      setV4Message("Rendering the mark...");

      const t3 = setTimeout(() => setV4Progress(75), 3000);
      const t4 = setTimeout(() => { setV4Progress(88); setV4Message("Almost there..."); }, 8000);

      if (parsed.imagePrompt) {
        const imgRes = await fetch("/api/generate-image", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: parsed.imagePrompt }) });
        const imgData = await imgRes.json();
        if (imgData.url) setLogoImage(imgData.url);
      }
      clearTimeout(t3); clearTimeout(t4);
      setV4Progress(100);
      setV4Message("✦");
      await new Promise(r => setTimeout(r, 600));
      setV4Phase("done");
    } catch(e) { console.log("Logo error:", e); setV4Phase("idle"); }
  };

  /* ═══ CHECKOUT — MEDIUM SELECTOR ═══ */
  const [checkoutMedium, setCheckoutMedium] = useState("crystal_standard");
  const [checkoutShipping, setCheckoutShipping] = useState({ name: "", email: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "" });
  const [checkoutGiftMsg, setCheckoutGiftMsg] = useState("");
  const [showGiftMsg, setShowGiftMsg] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const MEDIUM_OPTIONS = [
    { id: "crystal_standard", name: "Crystal block", subtitle: "Laser-engraved inside K9 optical crystal", price: 79, priceDisplay: "$79", includes: ["Medium crystal block (3.1\" × 4.7\")", "LED light base", "NFC decode card", "Gift box"], badge: "Most popular", featured: true },
    { id: "crystal_premium", name: "Premium crystal", subtitle: "Larger crystal with maximum engraving detail", price: 149, priceDisplay: "$149", includes: ["Large crystal block (3.9\" × 5.9\")", "Wood LED base with color-changing lights", "NFC decode card", "Premium gift box + wrapping"], badge: null, featured: false },
    { id: "acrylic_led", name: "Acrylic LED plaque", subtitle: "Full-color sculpture on crystal-clear acrylic", price: 29, priceDisplay: "$29", includes: ["Custom-shaped acrylic plaque", "Wood LED light base", "NFC decode card", "Gift box"], badge: "Best value", featured: false },
  ];

  // Gather the best available sculpture data for checkout
  const getSculptureData = () => {
    const nameEl = elements.find(e => e.startsWith("name:"));
    const recipientName = nameEl ? nameEl.split(":")[1].trim() : "";
    // Use the latest sculpture version available
    const bestSculpture = v3Sculpture || enhancedSculpture || sculpture;
    const bestImage = v3Image || enhancedImage || sculptureImage;
    return {
      name: bestSculpture?.name || "Custom Sculpture",
      imageUrl: bestImage,
      crystalRenderUrl: bestImage, // TODO: crystal-specific render
      decodeTitle: bestSculpture?.name,
      decodeStory: bestSculpture?.connections?.map(c => `${c.element}: ${c.match}`).join("\n\n") || "",
      decodeElements: bestSculpture?.connections || [],
      recipientName: recipientName.charAt(0).toUpperCase() + recipientName.slice(1),
      metadata: { elements, sculpture: bestSculpture, discoveries },
    };
  };

  const handleBuyProduct = (product) => {
    // Pre-fill recipient name from conversation
    const nameEl = elements.find(e => e.startsWith("name:"));
    if (nameEl) {
      const n = nameEl.split(":")[1].trim();
      setCheckoutShipping(prev => ({ ...prev, name: n.charAt(0).toUpperCase() + n.slice(1) }));
    }
    setPhase("checkout");
  };

  const handleCheckoutSubmit = async () => {
    setCheckoutLoading(true);
    const selected = MEDIUM_OPTIONS.find(m => m.id === checkoutMedium);
    const sculptureData = getSculptureData();
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          medium: checkoutMedium,
          priceCents: selected.price * 100,
          shipping: checkoutShipping,
          giftMessage: showGiftMsg ? checkoutGiftMsg : null,
          design: sculptureData,
        }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else { console.error("No checkout URL:", data); setCheckoutLoading(false); }
    } catch(e) { console.error("Checkout error:", e); setCheckoutLoading(false); }
  };

  const checkoutCanSubmit = checkoutShipping.name && checkoutShipping.line1 && checkoutShipping.city && checkoutShipping.state && checkoutShipping.postal_code && checkoutShipping.email;

  // Star chart
  const chart = useMemo(() => layoutStarChart(elements, 50, 50, isMobile ? 35 : 30), [elements, isMobile]);

  const getNodeColor = (i) => {
    const hues = [35, 28, 42, 20, 38, 30, 45, 25];
    const h = hues[i % hues.length];
    return { color: `hsl(${h},55%,65%)`, glow: `hsla(${h},55%,65%,0.3)` };
  };

  // Constellation lines — connect each star to 2 nearest neighbors
  const constellationLines = useMemo(() => {
    const nodes = chart.nodes || [];
    if (nodes.length < 2) return [];
    const lines = [];
    const used = new Set();
    nodes.forEach((n, i) => {
      const dists = nodes.map((m, j) => ({ j, d: Math.hypot(n.x - m.x, n.y - m.y) }))
        .filter(d => d.j !== i)
        .sort((a, b) => a.d - b.d);
      for (let k = 0; k < Math.min(2, dists.length); k++) {
        const key = [Math.min(i, dists[k].j), Math.max(i, dists[k].j)].join("-");
        if (!used.has(key)) {
          used.add(key);
          lines.push({ x1: n.x, y1: n.y, x2: nodes[dists[k].j].x, y2: nodes[dists[k].j].y });
        }
      }
    });
    return lines;
  }, [chart.nodes]);

  return (
    <div style={{ width: "100vw", height: "100vh", overflow: "hidden", background: "#08080c", fontFamily: SANS, position: "relative" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');
        html,body{margin:0;padding:0;overflow:hidden;background:#08080c}*{box-sizing:border-box}
        @keyframes pulse{0%,100%{opacity:0.4}50%{opacity:0.8}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes nodeIn{from{opacity:0;transform:scale(0)}to{opacity:1;transform:scale(1)}}
        @keyframes twinkle{0%,100%{opacity:0.05}50%{opacity:0.3}}
        @keyframes revealIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes sculptingPulse{0%,100%{opacity:0.3;transform:scale(1)}50%{opacity:0.7;transform:scale(1.05)}}
        @keyframes loadRing{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
        @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
        @keyframes goldShimmer{0%,100%{opacity:0.85}50%{opacity:1;text-shadow:0 0 20px rgba(201,169,110,0.4)}}
        @keyframes progressGlow{0%,100%{box-shadow:0 0 8px rgba(201,169,110,0.2)}50%{box-shadow:0 0 20px rgba(201,169,110,0.5)}}
        @keyframes messageFade{0%{opacity:0;transform:translateY(8px)}20%{opacity:1;transform:translateY(0)}80%{opacity:1}100%{opacity:1}}
        @keyframes breathe{0%,100%{opacity:0.4;transform:scale(0.98)}50%{opacity:0.7;transform:scale(1.02)}}
        ::-webkit-scrollbar{width:2px}::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:4px}
        textarea:focus{outline:none}
      `}</style>

      {/* ═══ STAR CHART (visible during chat + sculpting) ═══ */}
      {phase !== "reveal" && (
        <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>
          <div style={{
            position: "absolute", inset: 0,
            background: elements.length > 0
              ? `radial-gradient(ellipse at 50% 45%, hsla(35,40%,20%,${Math.min(0.25, elements.length * 0.035)}) 0%, transparent 60%)`
              : "none",
            transition: "all 2s ease",
          }} />
          {Array.from({ length: 60 }).map((_, i) => (
            <div key={i} style={{
              position: "absolute",
              left: (Math.sin(i * 3.7 + 1.2) * 48 + 50) + "%",
              top: (Math.cos(i * 2.3 + 0.7) * 48 + 50) + "%",
              width: i % 7 === 0 ? 2 : 1, height: i % 7 === 0 ? 2 : 1,
              borderRadius: "50%",
              background: `rgba(255,255,255,${0.04 + Math.min(0.15, elements.length * 0.015)})`,
              animation: `twinkle ${3 + i % 4}s ease-in-out infinite ${i * 0.15}s`,
            }} />
          ))}
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
            {chart.nodes.length > 0 && <circle cx="50" cy="50" r={chart.innerR} fill="none" stroke="rgba(201,169,110,0.06)" strokeWidth="0.15" strokeDasharray="1 2" />}
            {chart.nodes.some(n => n.ring === 1) && <circle cx="50" cy="50" r={chart.outerR} fill="none" stroke="rgba(201,169,110,0.04)" strokeWidth="0.1" strokeDasharray="1 3" />}
            {chart.nodes.length >= 2 && chart.nodes.slice(1).map((to, i) => {
              const from = chart.nodes[i];
              return <line key={i} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="rgba(201,169,110,0.08)" strokeWidth="0.12" />;
            })}
            {chart.nodes.filter(n => n.ring === 0).map((n, i) => (
              <line key={"c" + i} x1="50" y1="50" x2={n.x} y2={n.y} stroke="rgba(201,169,110,0.05)" strokeWidth="0.08" />
            ))}
          </svg>
          {chart.personName && phase !== "sculpting" && (
            <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", textAlign: "center", zIndex: 3 }}>
              <div style={{ fontSize: isMobile ? 32 : 42, fontFamily: SERIF, fontStyle: "italic", fontWeight: 300, color: "rgba(201,169,110,0.9)", textShadow: "0 0 30px rgba(201,169,110,0.3)", animation: "goldShimmer 3s ease-in-out infinite" }}>{chart.personName}</div>
              {chart.birthday && <div style={{ fontSize: 11, color: "rgba(201,169,110,0.35)", marginTop: 4, letterSpacing: "0.15em", textTransform: "uppercase" }}>{chart.birthday}</div>}
            </div>
          )}
          {!chart.personName && elements.length === 0 && (
            <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", textAlign: "center", opacity: 0.3 }}>
              <div style={{ fontSize: 14, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.2)" }}>Their universe begins here</div>
            </div>
          )}
          {chart.nodes.map((node, i) => {
            const c = getNodeColor(i);
            return (
              <div key={node.el} style={{ position: "absolute", left: node.x + "%", top: node.y + "%", transform: "translate(-50%,-50%)", animation: `nodeIn 0.6s ease ${0.3 + i * 0.15}s both`, pointerEvents: "none", zIndex: 2 }}>
                <div style={{ position: "absolute", inset: -20, background: `radial-gradient(circle, ${c.glow} 0%, transparent 70%)`, animation: `pulse 4s ease-in-out infinite ${i * 0.6}s` }} />
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.color, boxShadow: `0 0 10px ${c.glow}` }} />
                <div style={{ position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap", fontSize: 10, fontFamily: SERIF, fontStyle: "italic", color: "rgba(201,169,110,0.55)" }}>{node.el}</div>
              </div>
            );
          })}
          <a href="/" style={{ position: "absolute", top: isMobile ? 50 : 20, left: 16, fontSize: 14, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.2)", textDecoration: "none", zIndex: 4 }}>Yours Truly</a>
          {elements.length > 0 && <div style={{ position: "absolute", top: isMobile ? 50 : 20, right: 16, fontSize: 10, color: "rgba(201,169,110,0.3)", letterSpacing: "0.1em", zIndex: 4 }}>{elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:")).length} stars</div>}

          {/* ═══ CONSTELLATION LINES — draw during sculpting ═══ */}
          {phase === "sculpting" && constellationLines.length > 0 && (
            <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 3, pointerEvents: "none" }}>
              {constellationLines.map((line, i) => {
                const threshold = (i / constellationLines.length) * 90;
                const visible = sculptProgress >= threshold;
                const lineLen = Math.hypot((line.x2 - line.x1), (line.y2 - line.y1)) * 10;
                return (
                  <line key={i}
                    x1={line.x1 + "%"} y1={line.y1 + "%"}
                    x2={line.x2 + "%"} y2={line.y2 + "%"}
                    stroke="rgba(201,169,110,0.25)"
                    strokeWidth="0.5"
                    strokeDasharray={lineLen}
                    strokeDashoffset={visible ? 0 : lineLen}
                    style={{ transition: "stroke-dashoffset 2s ease, opacity 1s ease", opacity: visible ? 1 : 0 }}
                  />
                );
              })}
            </svg>
          )}

          {/* ═══ SCULPTING LOADING EXPERIENCE ═══ */}
          {phase === "sculpting" && (
            <div style={{
              position: "absolute", inset: 0, zIndex: 5,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              background: "radial-gradient(ellipse at 50% 40%, rgba(30,25,15,0.6) 0%, transparent 70%)",
            }}>
              {/* Name — large and glowing */}
              <div style={{
                fontSize: isMobile ? 38 : 54, fontFamily: SERIF, fontStyle: "italic", fontWeight: 300,
                color: "rgba(201,169,110,0.9)",
                textShadow: "0 0 40px rgba(201,169,110,0.3)",
                animation: "breathe 4s ease-in-out infinite",
                marginBottom: 40,
              }}>
                {sculptName}
              </div>

              {/* Progress bar container */}
              <div style={{ width: isMobile ? "75%" : 320, maxWidth: 320 }}>
                {/* Percentage */}
                <div style={{
                  textAlign: "right", marginBottom: 8,
                  fontSize: 12, fontFamily: SANS, fontWeight: 500, letterSpacing: "0.05em",
                  color: "rgba(201,169,110,0.5)",
                }}>
                  {Math.round(sculptProgress)}%
                </div>

                {/* Track */}
                <div style={{
                  width: "100%", height: 3, borderRadius: 2,
                  background: "rgba(201,169,110,0.08)",
                  overflow: "hidden",
                }}>
                  {/* Fill */}
                  <div style={{
                    width: sculptProgress + "%", height: "100%", borderRadius: 2,
                    background: "linear-gradient(90deg, rgba(201,169,110,0.4), rgba(201,169,110,0.8))",
                    transition: "width 2.5s cubic-bezier(0.25,0.46,0.45,0.94)",
                    animation: "progressGlow 2s ease-in-out infinite",
                  }} />
                </div>

                {/* Message */}
                <div key={sculptMessage} style={{
                  textAlign: "center", marginTop: 24,
                  fontSize: 14, fontFamily: SERIF, fontStyle: "italic",
                  color: "rgba(232,230,226,0.45)",
                  lineHeight: 1.6,
                  animation: "messageFade 0.8s ease forwards",
                  minHeight: 44,
                }}>
                  {sculptMessage}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ REVEAL ═══ */}
      {phase === "reveal" && sculpture && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 1,
          overflow: "auto", WebkitOverflowScrolling: "touch",
          background: "#08080c",
        }}>
          <div style={{ position: "fixed", inset: 0, background: "radial-gradient(ellipse at 50% 30%, hsla(35,40%,15%,0.3) 0%, transparent 60%)", pointerEvents: "none" }} />

          <div style={{ maxWidth: 600, margin: "0 auto", padding: isMobile ? "60px 20px 100px" : "80px 40px 100px" }}>

            {/* Sculpture name */}
            {revealStep >= 0 && (
              <div style={{ textAlign: "center", marginBottom: 32, animation: "revealIn 1s ease forwards" }}>
                <div style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 8 }}>
                  Made for {sculpture.personName}
                </div>
                <div style={{ fontSize: isMobile ? 36 : 52, fontFamily: SERIF, fontStyle: "italic", fontWeight: 300, color: "rgba(232,230,226,0.9)", lineHeight: 1.1, marginBottom: 8 }}>
                  {sculpture.name}
                </div>
                <div style={{ fontSize: 15, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.4)" }}>
                  {sculpture.subtitle}
                </div>
              </div>
            )}

            {/* Sculpture image or loading */}
            {revealStep >= 1 && (
              <div style={{ textAlign: "center", marginBottom: 32, animation: "revealIn 0.8s ease forwards" }}>
                {sculptureImage ? (
                  <img src={sculptureImage} alt={sculpture.name}
                    style={{
                      maxWidth: "100%", maxHeight: isMobile ? 380 : 480,
                      borderRadius: 16, objectFit: "contain",
                      filter: "drop-shadow(0 20px 60px rgba(0,0,0,0.6))",
                      animation: "revealIn 1.2s ease forwards",
                    }}
                  />
                ) : imageLoading ? (
                  <div style={{ padding: isMobile ? "50px 20px" : "70px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
                    {/* Loading ring */}
                    <div style={{
                      width: 48, height: 48, borderRadius: "50%",
                      border: "2px solid rgba(201,169,110,0.1)",
                      borderTopColor: "rgba(201,169,110,0.5)",
                      animation: "loadRing 1s linear infinite",
                    }} />
                    <div style={{ fontSize: 13, fontFamily: SERIF, fontStyle: "italic", color: "rgba(201,169,110,0.4)" }}>
                      Rendering your sculpture...
                    </div>
                    <div style={{
                      width: 200, height: 3, borderRadius: 2,
                      background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.3), transparent)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.5s ease-in-out infinite",
                    }} />
                  </div>
                ) : null}
              </div>
            )}

            {/* Form description */}
            {revealStep >= 1 && (
              <div style={{ textAlign: "center", marginBottom: 28, animation: "revealIn 0.8s ease forwards" }}>
                <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(201,169,110,0.35)", marginBottom: 6 }}>The Form</div>
                <div style={{ fontSize: 16, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.6)" }}>
                  {sculpture.dominantForm} — {sculpture.material}
                </div>
              </div>
            )}



            {/* Decode pill + slide-up panel (like gallery) */}
            {revealStep >= 2 && (
              <>
                {/* Decode toggle pill */}
                <div style={{ textAlign: "center", marginTop: 24, animation: "revealIn 0.8s ease forwards" }}>
                  <button onClick={() => { setDecodeOpen(!decodeOpen); setExpandedCard(null); }} style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "12px 24px", borderRadius: 24,
                    background: decodeOpen ? "rgba(201,169,110,0.15)" : "rgba(255,255,255,0.06)",
                    border: "1px solid " + (decodeOpen ? "rgba(201,169,110,0.4)" : "rgba(255,255,255,0.1)"),
                    color: decodeOpen ? "rgba(201,169,110,0.9)" : "rgba(232,230,226,0.6)",
                    fontSize: 12, fontWeight: 500, fontFamily: SANS, letterSpacing: "0.05em",
                    cursor: "pointer", transition: "all 0.3s ease",
                  }}>
                    <span style={{ fontSize: 14 }}>✦</span>
                    {decodeOpen ? "Hide" : "Decode this sculpture"}
                  </button>
                </div>

                {/* Decode slide-up panel */}
                <div style={{
                  maxHeight: decodeOpen ? 600 : 0,
                  opacity: decodeOpen ? 1 : 0,
                  overflow: "hidden",
                  transition: "all 0.5s cubic-bezier(0.16,1,0.3,1)",
                  marginTop: decodeOpen ? 16 : 0,
                }}>
                  <div style={{
                    padding: "20px 18px",
                    background: "rgba(10,10,18,0.6)",
                    backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
                    borderRadius: 16,
                    border: "1px solid rgba(201,169,110,0.1)",
                  }}>
                    <div style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 12 }}>
                      What you.re seeing
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {sculpture.connections?.map((c, i) => {
                        const open = expandedCard === i;
                        return (
                          <div key={i} onClick={() => setExpandedCard(open ? null : i)} style={{
                            borderRadius: 14, cursor: "pointer", padding: "14px 16px",
                            background: open ? "rgba(201,169,110,0.06)" : "rgba(255,255,255,0.02)",
                            border: "1px solid " + (open ? "rgba(201,169,110,0.2)" : "rgba(255,255,255,0.04)"),
                            transition: "all 0.3s ease",
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div>
                                <div style={{ fontSize: 15, fontFamily: SERIF, fontWeight: 500, color: "rgba(232,230,226,0.8)", marginBottom: 2 }}>{c.element}</div>
                                <div style={{ fontSize: 10, color: "rgba(201,169,110,0.5)", letterSpacing: "0.05em" }}>from "{c.from}"</div>
                              </div>
                              <div style={{ color: "rgba(201,169,110,0.4)", fontSize: 14, transition: "transform 0.3s ease", transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>›</div>
                            </div>
                            <div style={{
                              maxHeight: open ? 200 : 0, opacity: open ? 1 : 0,
                              overflow: "hidden", transition: "all 0.4s ease",
                              marginTop: open ? 12 : 0,
                            }}>
                              <div style={{ fontSize: 13, lineHeight: 1.7, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.5)", marginBottom: 8 }}>{c.match}</div>
                              <div style={{ fontSize: 9, color: "rgba(201,169,110,0.3)", letterSpacing: "0.1em" }}>{c.score}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ═══ ALL THREE TIERS ═══ */}
            {revealStep >= 2 + (sculpture.connections?.length || 0) && (
              <div style={{ animation: "revealIn 1s ease forwards" }}>
                {/* Hero image */}
                {sculptureImage && (
                  <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <img src={sculptureImage} alt={sculpture.name}
                      style={{
                        maxWidth: "100%", maxHeight: isMobile ? 380 : 480,
                        borderRadius: 16, objectFit: "contain",
                        filter: "drop-shadow(0 20px 60px rgba(201,169,110,0.15))",
                      }}
                    />
                  </div>
                )}

                <div style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", textAlign: "center", marginBottom: 20 }}>
                  Choose your format
                </div>

                {PRODUCTS.map((product, pi) => (
                  <div key={product.id} style={{ textAlign: "center", marginBottom: 28 }}>
                    {product.comingSoon ? (
                      <div style={{
                        display: "block", padding: "18px 20px", borderRadius: 16,
                        background: "rgba(255,255,255,0.015)",
                        border: "1px dashed rgba(255,255,255,0.06)",
                        opacity: 0.5,
                      }}>
                        <div style={{ fontSize: 14, fontWeight: 500, fontFamily: SANS, color: "rgba(201,169,110,0.5)" }}>{product.label}</div>
                        <div style={{ fontSize: 24, fontWeight: 600, fontFamily: SANS, color: "rgba(232,230,226,0.3)", margin: "4px 0" }}>{product.price}</div>
                        <div style={{ fontSize: 12, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.2)" }}>{product.desc}</div>
                        <div style={{ fontSize: 10, color: "rgba(201,169,110,0.3)", marginTop: 6, letterSpacing: "0.15em", textTransform: "uppercase" }}>Coming Soon</div>
                      </div>
                    ) : (
                    <a href={product.stripe} target="_blank" rel="noopener noreferrer" style={{
                      display: "block", padding: "18px 20px", borderRadius: 16,
                      background: "rgba(201,169,110,0.1)",
                      border: "1px solid rgba(201,169,110,0.25)",
                      textDecoration: "none", transition: "all 0.3s ease",
                    }}>
                      {product.sampleImage && (
                        <div style={{ marginBottom: 12 }}>
                          <img src={product.sampleImage} alt="Sample" style={{ maxHeight: 120, borderRadius: 8, objectFit: "contain", filter: "drop-shadow(0 4px 12px rgba(150,200,255,0.15))" }} />
                          <div style={{ fontSize: 9, color: "rgba(150,200,255,0.4)", fontStyle: "italic", fontFamily: SERIF, marginTop: 6 }}>Your design engraved inside crystal</div>
                        </div>
                      )}
                      <div style={{ fontSize: 14, fontWeight: 500, fontFamily: SANS, color: "rgba(201,169,110,0.9)" }}>{product.label}</div>
                      <div style={{ fontSize: 24, fontWeight: 600, fontFamily: SANS, color: "rgba(232,230,226,0.9)", margin: "4px 0" }}>{product.price}</div>
                      <div style={{ fontSize: 12, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.4)" }}>{product.desc}</div>
                      <div style={{ fontSize: 10, color: "rgba(232,230,226,0.25)", marginTop: 6 }}>{product.delivery}</div>
                    </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ═══ GO DEEPER — ENHANCE BUTTON ═══ */}
            {revealStep >= 2 + (sculpture.connections?.length || 0) && enhancePhase === "idle" && (
              <div style={{ textAlign: "center", marginTop: 40, animation: "revealIn 1s ease forwards" }}>
                <div style={{ width: 1, height: 40, background: "linear-gradient(to bottom, rgba(201,169,110,0.2), rgba(120,100,200,0.3))", margin: "0 auto 16px" }} />
                <button onClick={enhanceSculpture} style={{
                  display: "inline-flex", alignItems: "center", gap: 10,
                  padding: "14px 28px", borderRadius: 28,
                  background: "linear-gradient(135deg, rgba(120,100,200,0.12), rgba(80,60,180,0.08))",
                  border: "1px solid rgba(120,100,200,0.25)",
                  color: "rgba(180,170,220,0.9)",
                  fontSize: 13, fontWeight: 500, fontFamily: SANS, letterSpacing: "0.05em",
                  cursor: "pointer", transition: "all 0.3s ease",
                }}>
                  <span style={{ fontSize: 16 }}>◈</span>
                  Go Deeper — let the AI dream
                </button>
                <div style={{ marginTop: 8, fontSize: 10, color: "rgba(180,170,220,0.3)", fontStyle: "italic", fontFamily: SERIF }}>
                  Hidden connections. No rules.
                </div>
              </div>
            )}

            {/* ═══ ENHANCE LOADING ═══ */}
            {(enhancePhase === "discovering" || enhancePhase === "creating") && (
              <div style={{ marginTop: 40, padding: "40px 20px", textAlign: "center", animation: "revealIn 0.8s ease forwards" }}>
                <div style={{ width: 1, height: 30, background: "linear-gradient(to bottom, rgba(201,169,110,0.1), rgba(120,100,200,0.3))", margin: "0 auto 24px" }} />
                <div style={{
                  fontSize: isMobile ? 24 : 32, fontFamily: SERIF, fontStyle: "italic", fontWeight: 300,
                  color: "rgba(180,170,220,0.8)",
                  animation: "breathe 4s ease-in-out infinite",
                  marginBottom: 24,
                }}>
                  {enhancePhase === "discovering" ? "◈ Discovering" : "◈ Dreaming"}
                </div>

                {/* Progress bar */}
                <div style={{ width: isMobile ? "75%" : 280, margin: "0 auto" }}>
                  <div style={{ textAlign: "right", marginBottom: 6, fontSize: 11, fontFamily: SANS, color: "rgba(120,100,200,0.5)" }}>
                    {Math.round(enhanceProgress)}%
                  </div>
                  <div style={{ width: "100%", height: 3, borderRadius: 2, background: "rgba(120,100,200,0.08)", overflow: "hidden" }}>
                    <div style={{
                      width: enhanceProgress + "%", height: "100%", borderRadius: 2,
                      background: "linear-gradient(90deg, rgba(120,100,200,0.4), rgba(180,170,220,0.8))",
                      transition: "width 2.5s cubic-bezier(0.25,0.46,0.45,0.94)",
                    }} />
                  </div>
                  <div key={enhanceMessage} style={{
                    textAlign: "center", marginTop: 16,
                    fontSize: 13, fontFamily: SERIF, fontStyle: "italic",
                    color: "rgba(180,170,220,0.45)",
                    animation: "messageFade 0.8s ease forwards",
                  }}>
                    {enhanceMessage}
                  </div>
                </div>
              </div>
            )}

            {/* ═══ ENHANCED RESULT ═══ */}
            {enhancePhase === "done" && enhancedSculpture && (
              <div style={{ marginTop: 40, animation: "revealIn 1s ease forwards" }}>
                <div style={{ width: 1, height: 40, background: "linear-gradient(to bottom, rgba(201,169,110,0.1), rgba(120,100,200,0.4))", margin: "0 auto 24px" }} />

                {/* Discoveries section */}
                {discoveries?.discoveries && (
                  <div style={{
                    marginBottom: 32, padding: "20px 18px",
                    background: "rgba(120,100,200,0.04)",
                    border: "1px solid rgba(120,100,200,0.12)",
                    borderRadius: 16,
                  }}>
                    <div style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(120,100,200,0.5)", marginBottom: 14 }}>
                      ◈ What we discovered
                    </div>
                    {discoveries.discoveries.map((d, i) => (
                      <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: i < discoveries.discoveries.length - 1 ? "1px solid rgba(120,100,200,0.06)" : "none" }}>
                        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(120,100,200,0.4)", marginBottom: 4 }}>{d.type}</div>
                        <div style={{ fontSize: 14, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.6)", lineHeight: 1.6 }}>{d.finding}</div>
                      </div>
                    ))}
                    {discoveries.synthesis && (
                      <div style={{ marginTop: 8, fontSize: 14, fontFamily: SERIF, fontStyle: "italic", color: "rgba(180,170,220,0.7)", lineHeight: 1.6, padding: "12px 0 0", borderTop: "1px solid rgba(120,100,200,0.1)" }}>
                        "{discoveries.synthesis}"
                      </div>
                    )}
                  </div>
                )}

                {/* Sculpture name */}
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(120,100,200,0.4)", marginBottom: 8 }}>
                    The Deeper Design
                  </div>
                  <div style={{ fontSize: isMobile ? 32 : 44, fontFamily: SERIF, fontStyle: "italic", fontWeight: 300, color: "rgba(232,230,226,0.9)", lineHeight: 1.1, marginBottom: 8 }}>
                    {enhancedSculpture.name}
                  </div>
                  <div style={{ fontSize: 14, fontFamily: SERIF, fontStyle: "italic", color: "rgba(180,170,220,0.5)" }}>
                    {enhancedSculpture.subtitle}
                  </div>
                </div>

                {/* Image */}
                {enhancedImage && (
                  <div style={{ textAlign: "center", marginBottom: 24 }}>
                    <img src={enhancedImage} alt={enhancedSculpture.name}
                      style={{
                        maxWidth: "100%", maxHeight: isMobile ? 380 : 480,
                        borderRadius: 16, objectFit: "contain",
                        filter: "drop-shadow(0 20px 60px rgba(120,100,200,0.2))",
                        animation: "revealIn 1.2s ease forwards",
                      }}
                    />
                  </div>
                )}

                {/* Connections */}
                {enhancedSculpture.connections?.map((c, i) => (
                  <div key={i} style={{ padding: "10px 16px", marginBottom: 6 }}>
                    <div style={{ fontSize: 14, fontFamily: SERIF, fontWeight: 500, color: "rgba(232,230,226,0.75)" }}>{c.element}</div>
                    <div style={{ fontSize: 11, color: "rgba(120,100,200,0.4)", marginTop: 2 }}>from "{c.from}"</div>
                    <div style={{ fontSize: 12, fontFamily: SERIF, fontStyle: "italic", color: "rgba(180,170,220,0.35)", marginTop: 4, lineHeight: 1.6 }}>{c.match}</div>
                  </div>
                ))}

                {/* Buy carousel */}
                <div style={{ marginTop: 24 }}>
                  <SwipeCarousel sculptureImage={enhancedImage} sculptureName={enhancedSculpture.name} onBuy={handleBuyProduct} />
                </div>

                {/* ═══ V3 — WEIGHTED STARS ═══ */}
                {v3Phase === "idle" && (
                  <div style={{ textAlign: "center", marginTop: 40 }}>
                    <div style={{ width: 1, height: 30, background: "linear-gradient(to bottom, rgba(120,100,200,0.2), rgba(200,170,100,0.3))", margin: "0 auto 16px" }} />
                    <button onClick={() => { setV3Phase("weighting"); const w = {}; elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:")).forEach(e => w[e] = "normal"); setElementWeights(w); }} style={{
                      display: "inline-flex", alignItems: "center", gap: 10,
                      padding: "14px 28px", borderRadius: 28,
                      background: "linear-gradient(135deg, rgba(200,170,100,0.12), rgba(180,150,80,0.08))",
                      border: "1px solid rgba(200,170,100,0.25)",
                      color: "rgba(220,200,150,0.9)",
                      fontSize: 13, fontWeight: 500, fontFamily: SANS, letterSpacing: "0.05em",
                      cursor: "pointer",
                    }}>
                      <span style={{ fontSize: 16 }}>✧</span>
                      Customize — choose what matters most
                    </button>
                  </div>
                )}

                {v3Phase === "weighting" && (
                  <div style={{ marginTop: 40, animation: "revealIn 0.8s ease forwards" }}>
                    <div style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(200,170,100,0.5)", textAlign: "center", marginBottom: 16 }}>
                      Tap to weight — bright = must use, dim = exclude
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 20 }}>
                      {elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:")).map((el, i) => {
                        const w = elementWeights[el] || "normal";
                        const colors = { dimmed: { bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.05)", text: "rgba(255,255,255,0.2)" }, normal: { bg: "rgba(201,169,110,0.08)", border: "rgba(201,169,110,0.2)", text: "rgba(201,169,110,0.7)" }, boosted: { bg: "rgba(201,169,110,0.2)", border: "rgba(201,169,110,0.5)", text: "rgba(255,230,150,1)" } };
                        const c = colors[w];
                        return (
                          <button key={i} onClick={() => cycleWeight(el)} style={{
                            padding: "8px 14px", borderRadius: 20, cursor: "pointer",
                            background: c.bg, border: "1px solid " + c.border, color: c.text,
                            fontSize: 12, fontFamily: SERIF, fontStyle: "italic",
                            transition: "all 0.3s ease",
                            transform: w === "boosted" ? "scale(1.05)" : w === "dimmed" ? "scale(0.95)" : "scale(1)",
                          }}>
                            {w === "boosted" ? "⚡ " : w === "dimmed" ? "✕ " : ""}{el}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <button onClick={generateV3} style={{
                        padding: "14px 32px", borderRadius: 28,
                        background: "rgba(200,170,100,0.15)",
                        border: "1px solid rgba(200,170,100,0.3)",
                        color: "rgba(220,200,150,0.9)",
                        fontSize: 14, fontWeight: 500, fontFamily: SANS,
                        cursor: "pointer",
                      }}>
                        Generate with your weights
                      </button>
                    </div>
                  </div>
                )}

                {v3Phase === "creating" && (
                  <div style={{ marginTop: 40, textAlign: "center" }}>
                    <div style={{ fontSize: 20, fontFamily: SERIF, fontStyle: "italic", color: "rgba(200,170,100,0.7)", animation: "breathe 4s ease-in-out infinite", marginBottom: 16 }}>✧ Sculpting</div>
                    <div style={{ width: isMobile ? "75%" : 280, margin: "0 auto" }}>
                      <div style={{ textAlign: "right", fontSize: 11, color: "rgba(200,170,100,0.4)", marginBottom: 6 }}>{Math.round(v3Progress)}%</div>
                      <div style={{ width: "100%", height: 3, borderRadius: 2, background: "rgba(200,170,100,0.08)", overflow: "hidden" }}>
                        <div style={{ width: v3Progress + "%", height: "100%", borderRadius: 2, background: "linear-gradient(90deg, rgba(200,170,100,0.4), rgba(220,200,150,0.8))", transition: "width 2.5s ease" }} />
                      </div>
                      <div key={v3Message} style={{ textAlign: "center", marginTop: 12, fontSize: 13, fontFamily: SERIF, fontStyle: "italic", color: "rgba(200,170,100,0.4)", animation: "messageFade 0.8s ease forwards" }}>{v3Message}</div>
                    </div>
                  </div>
                )}

                {v3Phase === "done" && v3Sculpture && (
                  <div style={{ marginTop: 40, animation: "revealIn 1s ease forwards" }}>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(200,170,100,0.4)", marginBottom: 8 }}>Your Custom Design</div>
                      <div style={{ fontSize: isMobile ? 28 : 38, fontFamily: SERIF, fontStyle: "italic", fontWeight: 300, color: "rgba(232,230,226,0.9)", marginBottom: 6 }}>{v3Sculpture.name}</div>
                      <div style={{ fontSize: 13, fontFamily: SERIF, fontStyle: "italic", color: "rgba(200,170,100,0.5)" }}>{v3Sculpture.subtitle}</div>
                    </div>
                    {v3Image && (
                      <div style={{ textAlign: "center", marginBottom: 20 }}>
                        <img src={v3Image} alt={v3Sculpture.name} style={{ maxWidth: "100%", maxHeight: isMobile ? 380 : 480, borderRadius: 16, objectFit: "contain", filter: "drop-shadow(0 20px 60px rgba(200,170,100,0.2))" }} />
                      </div>
                    )}
                    {v3Sculpture.connections?.map((c, i) => (
                      <div key={i} style={{ padding: "10px 16px", marginBottom: 6 }}>
                        <div style={{ fontSize: 14, fontFamily: SERIF, fontWeight: 500, color: "rgba(232,230,226,0.75)" }}>{c.element}</div>
                        <div style={{ fontSize: 12, fontFamily: SERIF, fontStyle: "italic", color: "rgba(200,170,100,0.35)", marginTop: 4 }}>{c.match}</div>
                      </div>
                    ))}
                    <div style={{ marginTop: 16 }}>
                      <SwipeCarousel sculptureImage={v3Image} sculptureName={v3Sculpture.name} onBuy={handleBuyProduct} />
                    </div>
                  </div>
                )}

                {/* ═══ V4 — PERSONAL LOGO ═══ */}
                {(enhancePhase === "done" || v3Phase === "done") && v4Phase === "idle" && (
                  <div style={{ textAlign: "center", marginTop: 40 }}>
                    <div style={{ width: 1, height: 30, background: "linear-gradient(to bottom, rgba(120,100,200,0.1), rgba(232,230,226,0.2))", margin: "0 auto 16px" }} />
                    <button onClick={generateLogo} style={{
                      display: "inline-flex", alignItems: "center", gap: 10,
                      padding: "14px 28px", borderRadius: 28,
                      background: "rgba(232,230,226,0.06)",
                      border: "1px solid rgba(232,230,226,0.15)",
                      color: "rgba(232,230,226,0.7)",
                      fontSize: 13, fontWeight: 500, fontFamily: SANS, letterSpacing: "0.05em",
                      cursor: "pointer",
                    }}>
                      <span style={{ fontSize: 16 }}>◎</span>
                      Create their logo
                    </button>
                  </div>
                )}

                {v4Phase === "creating" && (
                  <div style={{ marginTop: 40, textAlign: "center" }}>
                    <div style={{ fontSize: 20, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.5)", animation: "breathe 4s ease-in-out infinite", marginBottom: 16 }}>◎ Designing</div>
                    <div style={{ width: isMobile ? "75%" : 280, margin: "0 auto" }}>
                      <div style={{ textAlign: "right", fontSize: 11, color: "rgba(232,230,226,0.3)", marginBottom: 6 }}>{Math.round(v4Progress)}%</div>
                      <div style={{ width: "100%", height: 3, borderRadius: 2, background: "rgba(232,230,226,0.06)", overflow: "hidden" }}>
                        <div style={{ width: v4Progress + "%", height: "100%", borderRadius: 2, background: "linear-gradient(90deg, rgba(232,230,226,0.3), rgba(232,230,226,0.7))", transition: "width 2.5s ease" }} />
                      </div>
                      <div key={v4Message} style={{ textAlign: "center", marginTop: 12, fontSize: 13, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.3)", animation: "messageFade 0.8s ease forwards" }}>{v4Message}</div>
                    </div>
                  </div>
                )}

                {v4Phase === "done" && logoData && (
                  <div style={{ marginTop: 40, animation: "revealIn 1s ease forwards" }}>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(232,230,226,0.3)", marginBottom: 8 }}>Their Logo</div>
                      <div style={{ fontSize: isMobile ? 28 : 38, fontFamily: SERIF, fontStyle: "italic", fontWeight: 300, color: "rgba(232,230,226,0.9)", marginBottom: 6 }}>{logoData.name}</div>
                      <div style={{ fontSize: 13, fontFamily: SERIF, fontStyle: "italic", color: "rgba(232,230,226,0.4)" }}>{logoData.concept}</div>
                    </div>
                    {logoImage && (
                      <div style={{ textAlign: "center", marginBottom: 20 }}>
                        <img src={logoImage} alt={logoData.name} style={{ maxWidth: isMobile ? 250 : 320, borderRadius: 12, objectFit: "contain", filter: "drop-shadow(0 10px 40px rgba(232,230,226,0.1))" }} />
                      </div>
                    )}
                    <div style={{ marginTop: 16 }}>
                      <SwipeCarousel sculptureImage={logoImage} sculptureName={logoData.name} onBuy={handleBuyProduct} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ CHECKOUT — MEDIUM SELECTOR ═══ */}
      {phase === "checkout" && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 30,
          background: "#0a0a0c",
          overflowY: "auto",
          color: "#fff",
        }}>
          <div style={{ maxWidth: 720, margin: "0 auto", padding: isMobile ? "30px 16px" : "50px 24px" }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 32 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: "rgba(201,169,110,0.4)", marginBottom: 8 }}>Yours Truly</div>
              <div style={{ fontSize: isMobile ? 24 : 32, fontFamily: SERIF, fontStyle: "italic", fontWeight: 300, color: "rgba(232,230,226,0.9)", marginBottom: 8 }}>Bring it to life</div>
              <div style={{ fontSize: 13, color: "rgba(232,230,226,0.4)" }}>
                Choose how to receive <span style={{ fontStyle: "italic", color: "rgba(232,230,226,0.7)" }}>{getSculptureData().name}</span>
              </div>
            </div>

            {/* Preview image */}
            {getSculptureData().imageUrl && (
              <div style={{ textAlign: "center", marginBottom: 28, background: "rgba(255,255,255,0.02)", borderRadius: 16, padding: 20 }}>
                <img src={getSculptureData().imageUrl} alt="Your sculpture" style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 12, objectFit: "contain" }} />
                <div style={{ marginTop: 10, fontSize: 11, fontFamily: SERIF, fontStyle: "italic", color: "rgba(201,169,110,0.35)" }}>
                  {checkoutMedium.startsWith("crystal") ? "Your sculpture laser-engraved inside crystal" : "Your sculpture printed on crystal-clear acrylic"}
                </div>
              </div>
            )}

            {/* Medium cards */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 28 }}>
              {MEDIUM_OPTIONS.map(m => (
                <div key={m.id} onClick={() => setCheckoutMedium(m.id)} style={{
                  background: checkoutMedium === m.id ? "rgba(201,169,110,0.06)" : "rgba(255,255,255,0.02)",
                  border: checkoutMedium === m.id ? "2px solid rgba(201,169,110,0.5)" : m.featured ? "1px solid rgba(201,169,110,0.15)" : "1px solid rgba(255,255,255,0.06)",
                  borderRadius: 16, padding: "20px 16px", cursor: "pointer", position: "relative",
                  transition: "all 0.2s ease",
                }}>
                  {m.badge && (
                    <div style={{ position: "absolute", top: -9, left: 16, fontSize: 10, fontWeight: 500, letterSpacing: "0.04em", padding: "2px 10px", borderRadius: 12, background: m.badge === "Most popular" ? "rgba(201,169,110,0.9)" : "rgba(232,230,226,0.15)", color: m.badge === "Most popular" ? "#0a0a0c" : "rgba(232,230,226,0.6)" }}>
                      {m.badge}
                    </div>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 500, color: "rgba(232,230,226,0.85)", marginBottom: 4 }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: "rgba(232,230,226,0.35)", marginBottom: 12, lineHeight: 1.4 }}>{m.subtitle}</div>
                  <div style={{ fontSize: 26, fontWeight: 300, color: "rgba(201,169,110,0.9)", fontFamily: SANS, marginBottom: 12 }}>{m.priceDisplay}</div>
                  {m.includes.map((inc, i) => (
                    <div key={i} style={{ fontSize: 12, color: "rgba(232,230,226,0.35)", padding: "2px 0", paddingLeft: 14, position: "relative" }}>
                      <span style={{ position: "absolute", left: 0, color: "rgba(120,200,140,0.5)", fontSize: 10 }}>✓</span>
                      {inc}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Shipping form */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "rgba(232,230,226,0.6)", marginBottom: 12 }}>Ship to</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {[
                  { field: "name", placeholder: "Recipient name", full: false },
                  { field: "email", placeholder: "Your email", full: false, type: "email" },
                  { field: "line1", placeholder: "Street address", full: true },
                  { field: "line2", placeholder: "Apt, suite (optional)", full: true },
                  { field: "city", placeholder: "City", full: false },
                  { field: "state", placeholder: "State", full: false },
                  { field: "postal_code", placeholder: "ZIP code", full: false },
                  { field: "phone", placeholder: "Phone (optional)", full: false },
                ].map(f => (
                  <input key={f.field} type={f.type || "text"} placeholder={f.placeholder}
                    value={checkoutShipping[f.field]}
                    onChange={e => setCheckoutShipping(prev => ({ ...prev, [f.field]: e.target.value }))}
                    style={{
                      gridColumn: f.full ? "1 / -1" : "auto",
                      padding: "10px 14px", borderRadius: 10,
                      background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                      color: "#fff", fontSize: 14, fontFamily: SANS,
                      outline: "none", width: "100%", boxSizing: "border-box",
                    }}
                  />
                ))}
              </div>
              <label onClick={() => setShowGiftMsg(!showGiftMsg)} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12, color: "rgba(232,230,226,0.35)", cursor: "pointer" }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, border: "1px solid rgba(255,255,255,0.12)", background: showGiftMsg ? "rgba(201,169,110,0.3)" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff" }}>{showGiftMsg ? "✓" : ""}</span>
                Add a personal message
              </label>
              {showGiftMsg && (
                <textarea placeholder="Your message to the recipient..." value={checkoutGiftMsg} onChange={e => setCheckoutGiftMsg(e.target.value)}
                  style={{ width: "100%", marginTop: 8, padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#fff", fontSize: 14, fontFamily: SANS, outline: "none", minHeight: 70, resize: "vertical", boxSizing: "border-box" }}
                />
              )}
            </div>

            {/* CTA */}
            <div style={{ textAlign: "center" }}>
              <button onClick={handleCheckoutSubmit} disabled={!checkoutCanSubmit || checkoutLoading}
                style={{
                  width: "100%", maxWidth: 400, padding: "15px 40px", borderRadius: 12,
                  background: checkoutCanSubmit && !checkoutLoading ? "rgba(201,169,110,0.9)" : "rgba(201,169,110,0.2)",
                  border: "none", color: checkoutCanSubmit ? "#0a0a0c" : "rgba(10,10,12,0.4)",
                  fontSize: 15, fontWeight: 600, fontFamily: SANS, cursor: checkoutCanSubmit ? "pointer" : "default",
                  transition: "all 0.2s ease",
                }}>
                {checkoutLoading ? "Creating your order..." : `Continue to payment · ${MEDIUM_OPTIONS.find(m => m.id === checkoutMedium)?.priceDisplay}`}
              </button>
              <div style={{ fontSize: 11, color: "rgba(232,230,226,0.2)", marginTop: 8 }}>Secure checkout via Stripe · Free shipping in the US</div>
              <button onClick={() => setPhase("reveal")} style={{ marginTop: 14, background: "none", border: "none", color: "rgba(232,230,226,0.3)", fontSize: 12, cursor: "pointer", fontFamily: SANS, textDecoration: "underline", textUnderlineOffset: 3 }}>
                ← Back to your sculpture
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TOGGLE PILL (chat phase only) ═══ */}
      {phase === "chat" && (
        <div
          onClick={() => { const next = !chatOpen; setChatOpen(next); if (next) setTimeout(() => inputRef.current?.focus(), 300); }}
          style={{
            position: "fixed", top: isMobile ? 50 : 20,
            left: "50%", transform: "translateX(-50%)", zIndex: 50,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            padding: "8px 18px", borderRadius: 16,
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
            backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
          }}
        >
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>✦</span>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 500, fontFamily: SANS }}>{chatOpen ? "Hide" : "Show chat"}</span>
        </div>
      )}

      {/* ═══ CHAT CARD ═══ */}
      <div style={{
        position: "fixed",
        top: isMobile ? 90 : 60,
        left: isMobile ? 8 : 40, right: isMobile ? 8 : 40,
        width: "auto", maxWidth: isMobile ? "auto" : 700,
        maxHeight: isMobile ? "55vh" : "70vh",
        zIndex: 20,
        opacity: chatOpen && phase === "chat" ? 1 : 0,
        transform: chatOpen && phase === "chat" ? "scaleY(1) scaleX(1)" : "scaleY(0) scaleX(0.3)",
        transformOrigin: "top center",
        transition: "transform 0.35s cubic-bezier(0.16,1,0.3,1), opacity 0.25s ease",
        display: "flex", flexDirection: "column",
        background: "rgba(18,18,22,0.75)",
        backdropFilter: "blur(40px)", WebkitBackdropFilter: "blur(40px)",
        border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16,
        boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        overflow: "hidden",
        pointerEvents: chatOpen && phase === "chat" ? "auto" : "none",
        color: "#fff",
      }}>
        <div style={{ padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,0.25)" }}>✦</span>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>Sculptor</span>
          </div>
          {elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:")).length > 0 && (
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", padding: "2px 8px", borderRadius: 6, background: "rgba(255,255,255,0.04)" }}>
              {elements.filter(e => !e.startsWith("name:") && !e.startsWith("birthday:")).length} found
            </span>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ alignSelf: msg.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%", animation: "fadeUp 0.25s ease" }}>
              <div style={{
                padding: "9px 13px",
                borderRadius: msg.role === "user" ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
                background: msg.role === "user" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255," + (msg.role === "user" ? "0.1" : "0.05") + ")",
                fontSize: 14, lineHeight: 1.55,
                color: msg.role === "user" ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.55)",
              }}>{msg.content}</div>
            </div>
          ))}
          {loading && (
            <div style={{ alignSelf: "flex-start", maxWidth: "88%" }}>
              <div style={{ padding: "9px 13px", borderRadius: "12px 12px 12px 3px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)", fontSize: 14, color: "rgba(255,255,255,0.2)" }}>
                <span style={{ animation: "pulse 1.5s infinite" }}>thinking...</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div style={{ padding: "8px 10px", paddingBottom: isMobile ? "calc(env(safe-area-inset-bottom,0px) + 8px)" : 10, borderTop: "1px solid rgba(255,255,255,0.05)", flexShrink: 0 }}>
          {ready && (
            <div onClick={createSculpture} style={{
              marginBottom: 6, padding: "10px 14px", borderRadius: 10,
              background: "rgba(201,169,110,0.1)", border: "1px solid rgba(201,169,110,0.25)",
              fontSize: 13, color: "rgba(201,169,110,0.8)", textAlign: "center", cursor: "pointer",
              fontWeight: 500,
            }}>
              ✦ Create their design
            </div>
          )}
          <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
            <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Tell me about them..." rows={1}
              style={{ flex: 1, resize: "none", padding: "9px 11px", borderRadius: 10, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.07)", color: "#fff", fontSize: 16, fontFamily: SANS, lineHeight: 1.4, maxHeight: 72, overflowY: "auto" }}
            />
            <button onClick={send} disabled={loading || !input.trim()} style={{
              width: 34, height: 34, borderRadius: 8,
              background: input.trim() ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255," + (input.trim() ? "0.12" : "0.05") + ")",
              color: input.trim() ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.12)",
              fontSize: 14, cursor: input.trim() ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>↑</button>
          </div>
        </div>
      </div>
    </div>
  );
}
